import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Spinner } from 'react-bootstrap';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YFormField } from '../../../ui/YFormField';
import { YDialog } from '../../../ui/dialog/YDialog';
import {
  defaultJoinRequestTitle,
  joinRequestErrorMessage,
  submitClubJoinRequest,
} from '../services/joinRequestService';
import { isProRequiredError } from '../../pro/entitlements';
import '../../../ui/dialog/yDialog.css';

/**
 * Non-member submits a membership request to a listed club president.
 */
export function RequestClubMembershipDialog({
  show,
  onHide,
  onProRequired,
  clubId,
  clubName,
  requireApproval = false,
  onSubmitted,
}) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!show) {
      setTitle('');
      setMessage('');
      setError('');
      setSuccess('');
      setSubmitting(false);
      return;
    }
    setTitle(defaultJoinRequestTitle(clubName));
  }, [show, clubName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clubId) return;
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setError('Add a short message introducing yourself.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const data = await submitClubJoinRequest(clubId, {
        title: title.trim() || defaultJoinRequestTitle(clubName),
        message: trimmedMessage,
      });
      const status = data?.status;
      const note =
        status === 'pending'
          ? 'You already have a pending request for this club.'
          : 'Your request was sent to the club president.';
      setSuccess(note);
      onSubmitted?.(data?.join_request);
    } catch (err) {
      if (isProRequiredError(err, 'clubs')) {
        onProRequired?.();
        return;
      }
      setError(joinRequestErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const formId = 'club-join-request-form';
  const intro = requireApproval
    ? 'This club reviews membership requests. Introduce yourself and why you want to join.'
    : 'Ask to join this club. The president can approve your request or share an invite code.';

  return (
    <YDialog
      show={show}
      onHide={onHide}
      title={clubName ? `Request to join ${clubName}` : 'Request club membership'}
      testId="club-join-request-dialog"
      error={error || undefined}
      footer={
        success ? (
          <YButton type="button" variant="primary" onClick={onHide}>
            Done
          </YButton>
        ) : (
          <>
            <YButton type="button" variant="secondary" onClick={onHide} disabled={submitting}>
              Cancel
            </YButton>
            <YButton type="submit" form={formId} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" role="status" />
                  Sending…
                </>
              ) : (
                'Send request'
              )}
            </YButton>
          </>
        )
      }
    >
      {success ? (
        <p className="clubs-page__dialog-note" role="status">
          {success}
        </p>
      ) : (
        <Form id={formId} onSubmit={handleSubmit}>
          <p className="clubs-page__dialog-note">{intro}</p>
          <YFormField
            label="Subject"
            htmlFor="club-join-request-title"
            labelClassName="y-label y-label--dialog"
            required
          >
            <YInput
              id="club-join-request-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={submitting}
            />
          </YFormField>
          <YFormField
            label="Message"
            htmlFor="club-join-request-message"
            labelClassName="y-label y-label--dialog"
            required
          >
            <YInput
              id="club-join-request-message"
              type="textarea"
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell the president a bit about yourself and your hunting experience…"
              required
              disabled={submitting}
            />
          </YFormField>
        </Form>
      )}
    </YDialog>
  );
}

RequestClubMembershipDialog.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onProRequired: PropTypes.func,
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clubName: PropTypes.string,
  requireApproval: PropTypes.bool,
  onSubmitted: PropTypes.func,
};

RequestClubMembershipDialog.defaultProps = {
  show: false,
  onProRequired: null,
  clubId: null,
  clubName: '',
  requireApproval: false,
  onSubmitted: null,
};
